import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Inbox, RefreshCcw, ExternalLink, MapPin, Copy, Search, UserPlus, CheckCircle2,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_META = {
  new: { label: 'Baru', color: 'text-blue-600 border-blue-400' },
  contacted: { label: 'Dihubungi', color: 'text-amber-600 border-amber-400' },
  survey_scheduled: { label: 'Survey', color: 'text-purple-600 border-purple-400' },
  approved: { label: 'Approved', color: 'text-teal-600 border-teal-400' },
  converted: { label: 'Sudah Jadi Pelanggan', color: 'text-emerald-600 border-emerald-400' },
  rejected: { label: 'Ditolak', color: 'text-red-600 border-red-400' },
};

const RegistrationInbox = () => {
  const { selectedBranch: globalBranch } = useApp();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState(null); // { account_number, pppoe_username, pppoe_password, name }
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // { id, name } | null
  const [deleting, setDeleting] = useState(false);
  // 2026-07-18: bulk-select state for mass delete
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const { user } = useAuth();
  const userRole = (user?.role || '').toLowerCase();
  const canDelete = ['super_admin', 'superadmin', 'admin', 'owner', 'kepala_cabang'].includes(userRole);

  useEffect(() => {
    if (globalBranch && globalBranch !== 'all') setBranchFilter(String(globalBranch));
    else setBranchFilter('');
  }, [globalBranch]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (branchFilter) params.set('branch_id', branchFilter);
      if (search) params.set('search', search);
      params.set('limit', '50');
      const res = await axios.get(
        `${API_URL}/api/v2/registrations?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRows(res.data?.data || []);
      setStats(res.data?.stats || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, branchFilter, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        API_URL + '/api/v2/registrations/' + showDeleteConfirm.id,
        { headers: { Authorization: 'Bearer ' + token } },
      );
      toast.success('Prospek pendaftaran berhasil dihapus');
      setShowDeleteConfirm(null);
      setSelectedIds((prev) => {
        const nx = new Set(prev); nx.delete(showDeleteConfirm.id); return nx;
      });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Gagal menghapus prospek');
    } finally {
      setDeleting(false);
    }
  };

  // 2026-07-18: bulk delete — hormati guard converted per role
  const canDeleteConverted = ['super_admin','superadmin','admin','kepala_cabang'].includes(userRole);
  const isRowDeletable = (r) => {
    if (!canDelete) return false;
    const isConverted = String(r.status || '').toLowerCase() === 'converted';
    return !(isConverted && !canDeleteConverted);
  };
  const deletableRows = rows.filter(isRowDeletable);
  const allDeletableSelected = deletableRows.length > 0 && deletableRows.every(r => selectedIds.has(r.id));
  const someDeletableSelected = !allDeletableSelected && deletableRows.some(r => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    if (allDeletableSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(deletableRows.map(r => r.id)));
  };
  const toggleSelectOne = (id) => {
    setSelectedIds((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        API_URL + '/api/v2/registrations/bulk-delete',
        { ids: Array.from(selectedIds) },
        { headers: { Authorization: 'Bearer ' + token } },
      );
      const msg = data.skipped_count > 0
        ? `Berhasil hapus ${data.deleted_count}. ${data.skipped_count} baris dilewati (${(data.skipped || []).slice(0,3).map(s=>s.reason).join('; ')}${data.skipped_count>3?'…':''}).`
        : `Berhasil hapus ${data.deleted_count} prospek.`;
      toast.success(msg, { duration: 6000 });
      setSelectedIds(new Set());
      setShowBulkDeleteConfirm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Bulk delete gagal');
    } finally {
      setBulkDeleting(false);
    }
  };


  const updateStatus = async (id, status, notes) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_URL}/api/v2/registrations/${id}`,
        { status, notes },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Status diperbarui');
      fetchData();
      setSelectedRow(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (slug) => {
    // Prefer short registration domain if available
    const shortHost = 'daftar.kumarahotspot.com';
    const isSameHost = typeof window !== 'undefined'
      && (window.location.hostname === shortHost || window.location.hostname.endsWith('.kumarahotspot.com'));
    const url = isSameHost
      ? `https://${shortHost}/${slug}`
      : `${window.location.origin}/register/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link disalin: ' + url);
  };

  const doConvert = async () => {
    if (!selectedRow) return;
    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/v2/registrations/${selectedRow.id}/convert`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        setConvertResult(res.data.customer);
        setShowConvertConfirm(false);
        toast.success('Pendaftar berhasil dikonversi menjadi pelanggan');
        fetchData();
      } else {
        toast.error(res.data?.message || 'Konversi gagal');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setConverting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-4 md:p-6 space-y-6" data-testid="registration-inbox-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Inbox className="w-7 h-7 text-indigo-600" />
            Pendaftaran Baru
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Prospek pelanggan dari link registrasi publik per cabang.
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300" data-testid="branch-filter-chip">
            Cabang aktif: <b>{branchFilter ? (rows.find(r => String(r.branch_id) === String(branchFilter))?.branch_name || `#${branchFilter}`) : 'Semua Cabang'}</b>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
          <RefreshCcw className="w-4 h-4 mr-1"/>Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {[
          { k: 'new', label: 'Baru' },
          { k: 'contacted', label: 'Dihubungi' },
          { k: 'survey_scheduled', label: 'Survey' },
          { k: 'approved', label: 'Approved' },
          { k: 'converted', label: 'Jadi' },
          { k: 'rejected', label: 'Ditolak' },
        ].map((s) => (
          <button
            key={s.k}
            type="button"
            onClick={() => setStatusFilter(statusFilter === s.k ? '' : s.k)}
            className={`h-24 flex flex-col justify-center px-3 rounded-lg border bg-transparent transition ${statusFilter === s.k ? 'border-indigo-500 ring-2 ring-indigo-200' : 'border-slate-300 dark:border-slate-700'}`}
            data-testid={`stat-${s.k}`}
          >
            <div className="text-[10px] text-slate-500 uppercase tracking-wide">{s.label}</div>
            <div className={`text-2xl font-bold ${STATUS_META[s.k]?.color?.split(' ')[0] || 'text-slate-700'}`}>
              {stats[s.k] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs text-slate-500">Cari Nama / HP / NIK</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Ketik untuk cari..." className="pl-8" data-testid="search-input"/>
          </div>
        </div>
        <div className="w-full sm:w-auto sm:min-w-[160px]">
          <label className="text-xs text-slate-500">Status</label>
          <Select value={statusFilter || 'all'} onValueChange={(v)=>setStatusFilter(v==='all'?'':v)}>
            <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Semua"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Status</SelectItem>
              {Object.entries(STATUS_META).map(([k,v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      {/* 2026-07-18 Bulk-delete toolbar */}
      {canDelete && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-red-300 bg-red-50 px-4 py-2 dark:bg-red-900/20 dark:border-red-800" data-testid="bulk-delete-toolbar">
          <div className="text-sm text-red-800 dark:text-red-300">
            <b>{selectedIds.size}</b> baris terpilih untuk dihapus
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} data-testid="bulk-cancel-btn">
              Batal
            </Button>
            <Button
              size="sm"
              onClick={() => setShowBulkDeleteConfirm(true)}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" /> Hapus Terpilih
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-transparent overflow-x-auto">
        <table className="w-full text-sm" data-testid="registration-table">
          <thead className="bg-slate-100 dark:bg-slate-800">
            <tr>
              {canDelete && (
                <th className="px-3 py-2 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={allDeletableSelected}
                    ref={(el) => { if (el) el.indeterminate = someDeletableSelected; }}
                    onChange={toggleSelectAll}
                    disabled={deletableRows.length === 0}
                    className="w-4 h-4 cursor-pointer accent-red-600 disabled:opacity-40"
                    data-testid="select-all-registrations"
                    title="Pilih semua baris yang bisa dihapus"
                  />
                </th>
              )}
              <th className="text-left px-3 py-2 whitespace-nowrap">Tanggal</th>
              <th className="text-left px-3 py-2">Nama</th>
              <th className="text-left px-3 py-2">HP</th>
              <th className="text-left px-3 py-2">Cabang</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canDelete ? 7 : 6} className="text-center py-6 text-slate-500">Memuat...</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={canDelete ? 7 : 6} className="text-center py-6 text-slate-500" data-testid="empty-state">Belum ada pendaftaran.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className={`border-t border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 ${selectedIds.has(r.id) ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                {canDelete && (
                  <td className="px-3 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleSelectOne(r.id)}
                      disabled={!isRowDeletable(r)}
                      className="w-4 h-4 cursor-pointer accent-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`row-checkbox-${r.id}`}
                      title={isRowDeletable(r) ? 'Pilih untuk hapus massal' : 'Baris ini tidak bisa dihapus oleh role Anda'}
                    />
                  </td>
                )}
                <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-500">{r.created_at?.split(' ')[0]}</td>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{r.phone}</td>
                <td className="px-3 py-2 whitespace-nowrap text-xs">{r.branch_name}</td>
                <td className="px-3 py-2">
                  <Badge variant="outline" className={`${STATUS_META[r.status]?.color} min-w-[100px] justify-center`}>{STATUS_META[r.status]?.label || r.status}</Badge>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={()=>setSelectedRow(r)} data-testid={`detail-${r.id}`}>Detail</Button>
                    {canDelete && (() => {
                      const isConverted = String(r.status || '').toLowerCase() === 'converted';
                      const canDeleteConverted = ['super_admin','superadmin','admin','kepala_cabang'].includes(userRole);
                      const disabled = isConverted && !canDeleteConverted;
                      return (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={disabled}
                          onClick={() => setShowDeleteConfirm({ id: r.id, name: r.name })}
                          title={disabled ? 'Prospek "Sudah Jadi Pelanggan" tidak bisa dihapus oleh role Anda. Data pelanggan tidak akan terhapus.' : 'Hapus prospek pendaftaran ini (customer aktif tetap aman)'}
                          data-testid={`delete-${r.id}`}
                          className={disabled
                            ? 'border-slate-200 text-slate-400 cursor-not-allowed'
                            : 'border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      );
                    })()}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedRow} onOpenChange={(o)=>!o && setSelectedRow(null)}>
        <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle>Detail Pendaftaran #{selectedRow?.id}</DialogTitle>
          </DialogHeader>
          {selectedRow && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><b>Nama</b><br/>{selectedRow.name}</div>
                <div><b>HP</b><br/><a href={`https://wa.me/${(selectedRow.phone||'').replace(/^0/, '62')}`} target="_blank" rel="noreferrer" className="text-emerald-600 underline">{selectedRow.phone}</a></div>
                <div><b>Email</b><br/>{selectedRow.email || '—'}</div>
                <div><b>NIK</b><br/>{selectedRow.nik || '—'}</div>
                <div className="col-span-2"><b>Alamat</b><br/>{selectedRow.address}</div>
                {selectedRow.latitude && (
                  <div className="col-span-2 flex items-center gap-2 text-xs">
                    <MapPin className="w-4 h-4 text-red-500"/>
                    <a href={`https://www.google.com/maps?q=${selectedRow.latitude},${selectedRow.longitude}`} target="_blank" rel="noreferrer" className="text-indigo-600 underline">
                      {selectedRow.latitude}, {selectedRow.longitude} — Buka di Google Maps
                    </a>
                  </div>
                )}
                <div><b>Cabang</b><br/>{selectedRow.branch_name}</div>
                <div><b>Paket diminati</b><br/>{selectedRow.service_plan_name || '—'} {selectedRow.service_plan_price ? `(Rp ${Number(selectedRow.service_plan_price).toLocaleString('id-ID')})` : ''}</div>
                {selectedRow.notes && <div className="col-span-2"><b>Catatan Pendaftar</b><br/>{selectedRow.notes}</div>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {selectedRow.ktp_photo_url && (
                  <div>
                    <b className="text-xs">Foto KTP</b>
                    <a href={`${API_URL}${selectedRow.ktp_photo_url}`} target="_blank" rel="noreferrer">
                      <img src={`${API_URL}${selectedRow.ktp_photo_url}`} alt="KTP" className="mt-1 w-full h-32 object-cover rounded border"/>
                    </a>
                  </div>
                )}
                {selectedRow.house_photo_url && (
                  <div>
                    <b className="text-xs">Foto Rumah</b>
                    <a href={`${API_URL}${selectedRow.house_photo_url}`} target="_blank" rel="noreferrer">
                      <img src={`${API_URL}${selectedRow.house_photo_url}`} alt="Rumah" className="mt-1 w-full h-32 object-cover rounded border"/>
                    </a>
                  </div>
                )}
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="text-xs text-slate-500">Update Status</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_META).filter(([k]) => k !== 'converted').map(([k,v]) => (
                    <Button
                      key={k}
                      size="sm"
                      variant={selectedRow.status === k ? 'default' : 'outline'}
                      disabled={saving}
                      onClick={() => updateStatus(selectedRow.id, k, selectedRow.notes || '')}
                      data-testid={`btn-status-${k}`}
                    >
                      {v.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Konversi ke Pelanggan */}
              {selectedRow.status !== 'converted' && selectedRow.status !== 'rejected' && (
                <div className="border-t pt-3 space-y-2">
                  <div className="text-xs text-slate-500">Aksi Final</div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={converting}
                    onClick={() => setShowConvertConfirm(true)}
                    data-testid="btn-convert-to-customer"
                  >
                    <UserPlus className="w-4 h-4 mr-1" />
                    Konversi ke Pelanggan
                  </Button>
                  <p className="text-[11px] text-slate-500">
                    Sistem otomatis buat No.Pelanggan + PPPoE, kirim notifikasi ke owner cabang via WA + Email.
                  </p>
                </div>
              )}
              {selectedRow.status === 'converted' && (
                <div className="border-t pt-3">
                  <Badge variant="outline" className="text-emerald-600 border-emerald-400">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                    Sudah Jadi Pelanggan #{selectedRow.converted_customer_id || '—'}
                  </Badge>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Konversi Dialog */}
      <Dialog open={showConvertConfirm} onOpenChange={(o)=>!o && setShowConvertConfirm(false)}>
        <DialogContent className="max-w-md w-[95vw]" data-testid="convert-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Konversi ke Pelanggan?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Data pendaftar <b>{selectedRow?.name}</b> akan diubah menjadi pelanggan aktif.
              Sistem akan:
            </p>
            <ul className="list-disc pl-5 text-slate-600 dark:text-slate-400 space-y-1 text-xs">
              <li>Membuat <b>No. Pelanggan</b> otomatis (YYMMDD+urut)</li>
              <li>Membuat <b>Username &amp; Password PPPoE</b> otomatis</li>
              <li>Menempelkan data cabang &amp; alamat sesuai pendaftaran</li>
              <li>Mengirim notifikasi WhatsApp + Email ke owner cabang</li>
            </ul>
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Setelah konversi, silakan ubah paket layanan &amp; jadwalkan instalasi di halaman Pelanggan.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" disabled={converting} onClick={() => setShowConvertConfirm(false)} data-testid="convert-cancel">
                Batal
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={converting}
                onClick={doConvert}
                data-testid="convert-confirm"
              >
                {converting ? 'Mengonversi...' : 'Ya, Konversi Sekarang'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Success Result Dialog */}
      <Dialog open={!!convertResult} onOpenChange={(o)=>!o && setConvertResult(null)}>
        <DialogContent className="max-w-md w-[95vw]" data-testid="convert-success-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5" /> Konversi Berhasil
            </DialogTitle>
          </DialogHeader>
          {convertResult && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600 dark:text-slate-400">
                Pelanggan <b>{convertResult.name}</b> berhasil dibuat.
              </p>
              <div className="rounded-md border border-slate-300 dark:border-slate-700 divide-y divide-slate-200 dark:divide-slate-800 text-sm">
                {[
                  { label: 'No. Pelanggan', value: convertResult.account_number },
                  { label: 'PPPoE Username', value: convertResult.pppoe_username },
                  { label: 'PPPoE Password', value: convertResult.pppoe_password },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between px-3 py-2">
                    <div>
                      <div className="text-[10px] uppercase text-slate-500">{row.label}</div>
                      <div className="font-mono text-slate-800 dark:text-slate-100">{row.value}</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { navigator.clipboard.writeText(row.value || ''); toast.success('Disalin'); }}
                      data-testid={`copy-${row.label.replace(/\s+/g,'-').toLowerCase()}`}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500">
                Notifikasi WhatsApp &amp; Email ke owner cabang sedang dikirim di background.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setConvertResult(null)} data-testid="convert-close">Tutup</Button>
                <a
                  href={`/customers?search=${encodeURIComponent(convertResult.account_number)}`}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                  data-testid="convert-open-customer"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Buka Data Pelanggan
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={(o)=>!o && setShowDeleteConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Hapus Prospek Pendaftaran?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">Anda akan menghapus prospek atas nama:</p>
            <div className="font-semibold text-base text-slate-900 dark:text-slate-100 bg-slate-100 dark:bg-slate-800 rounded px-3 py-2">
              {showDeleteConfirm?.name}
            </div>
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300">
              <b>Aman:</b> Yang dihapus hanya baris pendaftaran (prospek).
              Kalau prospek ini sudah menjadi pelanggan, <b>data pelanggan di menu Pelanggan
              TIDAK akan terhapus</b>. Prospek "Sudah Jadi Pelanggan" hanya bisa dihapus oleh
              Admin/Super Admin.
            </div>
            <p className="text-xs text-red-600 dark:text-red-400">
              Baris pendaftaran ini akan dihapus permanen dari inbox dan tidak bisa dikembalikan.
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={()=>setShowDeleteConfirm(null)} disabled={deleting} data-testid="delete-cancel-btn">
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Menghapus...' : 'Ya, Hapus'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 2026-07-18 Bulk delete confirmation dialog */}
      <Dialog open={showBulkDeleteConfirm} onOpenChange={(o)=>!o && setShowBulkDeleteConfirm(false)}>
        <DialogContent className="max-w-md" data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Hapus {selectedIds.size} Prospek Sekaligus?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm">
              Anda akan menghapus <b>{selectedIds.size}</b> baris pendaftaran sekaligus.
            </p>
            <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-800 dark:text-emerald-300">
              <b>Aman:</b> Yang dihapus hanya baris di inbox pendaftaran. <b>Data pelanggan
              di menu Pelanggan TIDAK akan terhapus</b>. Baris di luar scope Anda atau yang
              tidak boleh dihapus akan otomatis dilewati (skipped) tanpa error.
            </div>
            <p className="text-xs text-red-600 dark:text-red-400">
              Aksi ini bersifat permanen dan tidak bisa dibatalkan.
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={() => setShowBulkDeleteConfirm(false)} disabled={bulkDeleting} data-testid="bulk-delete-cancel-btn">
              Batal
            </Button>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? 'Menghapus...' : `Ya, Hapus ${selectedIds.size}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegistrationInbox;
